import type {Chapter, Course, Lesson} from "../api/courcesService";

// Types for lesson and chapter progress info derived from calculated variables
export interface LessonProgressInfo {
  lesson: Lesson;
  lessonIcon: string;
  iconAlt: string;
  isClickable: boolean;
  isAvailable: boolean;
  isFinished: boolean;
  isCurrentLesson: boolean;
  isFirstAvailable: boolean;
  isNextAvailable: boolean;
}

export interface ChapterProgressInfo {
  lessonsCompleted: number;
  hasStarted: boolean;
  isChapterFinished: boolean;
  chapterIdx: number;
  chapterInfo: LessonProgressInfo[];
}

export type GetProgressInfoReturn = ChapterProgressInfo[];

export const getProgressInfo = (course: Course, courseWithProgress: Course): GetProgressInfoReturn => {
  const chapters = course.chapters || [];
  return chapters.map((chapter, chapterIdx) => {
    const chapterInfo: LessonProgressInfo[] = chapter.lessons.map((lesson, lessonIdx) => {
      // Check if lesson is finished using progressInfo
      const isFinished = courseWithProgress?.progressInfo?.finishedLessons.some(
        finishedLesson => finishedLesson.id === lesson.id
      ) || false;

      // Check if this is the current lesson (from progressInfo.lesson)
      const isCurrentLesson = courseWithProgress?.progressInfo?.lesson?.id === lesson.id;

      // Check if this is the first lesson in the first chapter and no progress yet
      const isFirstLessonInFirstChapter = chapterIdx === 0 && lessonIdx === 0;
      const hasNoProgress = !courseWithProgress?.progressInfo?.startedLessons?.length &&
        !courseWithProgress?.progressInfo?.finishedLessons?.length;
      const isFirstAvailable = isFirstLessonInFirstChapter && hasNoProgress;

      // Check if this is the next available lesson (previous lesson is finished)
      const isNextAvailable = (() => {
        if (isFinished || isCurrentLesson || isFirstAvailable) return false;

        // Flatten all lessons in order across all chapters
        const allLessons: { lesson: Lesson; chapter: Chapter; chapterIdx: number; lessonIdx: number }[] = [];
        course.chapters?.forEach((ch, chIdx) => {
          ch.lessons?.forEach((ls, lsIdx) => {
            allLessons.push({lesson: ls, chapter: ch, chapterIdx: chIdx, lessonIdx: lsIdx});
          });
        });

        // Find the current lesson's position
        const currentLessonIndex = allLessons.findIndex(
          item => item.lesson.id === lesson.id && item.chapterIdx === chapterIdx && item.lessonIdx === lessonIdx
        );

        if (currentLessonIndex === -1 || currentLessonIndex === 0) return false;

        // Check if the previous lesson is finished
        const previousLesson = allLessons[currentLessonIndex - 1];
        return courseWithProgress?.progressInfo?.finishedLessons.some(
          finishedLesson => finishedLesson.id === previousLesson.lesson.id
        ) || false;
      })();



      // Determine which icon to show
      let lessonIcon = '/images/ui/lessons-lock.png';
      let iconAlt = 'Locked';

      if (isFinished) {
        lessonIcon = '/images/ui/lessons-finished.png';
        iconAlt = 'Completed';
      } else if (isCurrentLesson) {
        lessonIcon = '/images/ui/lessons-available.png';
        iconAlt = 'Current';
      } else if (isFirstAvailable) {
        lessonIcon = '/images/ui/lessons-available.png';
        iconAlt = 'Available';
      } else if (isNextAvailable) {
        lessonIcon = '/images/ui/lessons-available.png';
        iconAlt = 'Available';
      }

      const isClickable = isFinished || isCurrentLesson || isFirstAvailable || isNextAvailable;
      const isAvailable = isFirstAvailable || isNextAvailable || isClickable;

      return {
        lesson,
        lessonIcon,
        iconAlt,
        isClickable,
        isAvailable,
        isFinished,
        isCurrentLesson,
        isFirstAvailable,
        isNextAvailable,
      } as LessonProgressInfo;
    })
    return {
      lessonsCompleted: chapterInfo.filter(lesson => lesson.isFinished).length,
      hasStarted: chapterInfo.filter(lesson => lesson.isAvailable).length > 0,
      isChapterFinished: chapterInfo.every(lesson => lesson.isFinished),
      chapterIdx: chapterIdx,
      chapterInfo,
    } as ChapterProgressInfo;
  });
}
